import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { StaffPerformance, StaffMonthlyTrendStats } from '../../types/SupportTypes';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Support Member Detail Component Props
export interface SupportMemberDetailProps {
  member: StaffPerformance | null;
  monthlyTrendStats: StaffMonthlyTrendStats;
  snapshotStartDate: Date;
  snapshotEndDate: Date;
}

const SupportMemberDetail: React.FC<SupportMemberDetailProps> = ({
  member,
  monthlyTrendStats,
  snapshotStartDate,
  snapshotEndDate
}) => {
  const formatResolutionTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes.toFixed(0)}m`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = Math.floor(minutes % 60);
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    } else {
      const days = Math.floor(minutes / 1440);
      const remainingMinutes = minutes % 1440;
      const hours = Math.floor(remainingMinutes / 60);
      if (hours > 0) {
        return `${days}d ${hours}h`;
      } else {
        return `${days}d`;
      }
    }
  };

  // Generate a complete list of months between snapshotStartDate and snapshotEndDate
  const standardizedMonths = useMemo(() => {
    const months = [];
    const startDate = new Date(snapshotStartDate);
    const endDate = new Date(snapshotEndDate);
    
    // Set to first day of month to ensure proper comparison
    const currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const lastDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    
    while (currentDate <= lastDate) {
      const year = currentDate.getFullYear();
      // Add leading zero for months 1-9
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      months.push(`${year}-${month}`);
      
      // Move to next month
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return months;
  }, [snapshotStartDate, snapshotEndDate]);
  
  // Get monthly data for the selected member with standardized months
  const memberMonthlyData = useMemo(() => {
    if (!member) {
      return { months: standardizedMonths, data: Array(standardizedMonths.length).fill(null) };
    }
    
    const memberData = monthlyTrendStats[member.name];
    if (!memberData) {
      return { months: standardizedMonths, data: Array(standardizedMonths.length).fill(null) };
    }
    
    // Map each standardized month to its data or null if no data exists
    const data = standardizedMonths.map(month => {
      return memberData[month] || null;
    });

    return {
      months: standardizedMonths,
      data
    };
  }, [member, monthlyTrendStats, standardizedMonths]);

  if (!member) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
        color: '#666'
      }}>
        <h3>Select a team member to view detailed statistics</h3>
        <p>Click on any team member card to see their performance metrics and trends.</p>
      </div>
    );
  }

  // Chart data for cases trend - using all standardized months
  const casesChartData = {
    labels: memberMonthlyData.months,
    datasets: [
      {
        label: 'Cases Assigned',
        data: memberMonthlyData.data.map(d => d?.assignedCases || 0),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true
      },
      {
        label: 'Cases Resolved',
        data: memberMonthlyData.data.map(d => d?.resolvedCases || 0),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  // Chart data for resolution time trend - using all standardized months
  const resolutionTimeChartData = {
    labels: memberMonthlyData.months,
    datasets: [
      {
        label: 'Avg Resolution Time (hours)',
        data: memberMonthlyData.data.map(d => (d?.avgResolutionTime || 0) / 60),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        fill: true
      },
      {
        label: 'First Response Time (hours)',
        data: memberMonthlyData.data.map(d => (d?.avgFirstResponseTime || 0) / 60),
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const resolutionRate = member.assignedCases ?
    ((member.resolvedCases || 0) / member.assignedCases * 100).toFixed(1) : '0.0';

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      height: '100%'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{
          margin: '0 0 8px 0',
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#1f2937'
        }}>
          {member.name}
        </h2>
        <p style={{
          margin: 0,
          color: '#6b7280',
          fontSize: '16px'
        }}>
          Support Team Member Performance (across snapshot period)
        </p>
      </div>

      {/* Key Metrics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Assigned Cases - Open/Total</h3>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
          {member.stillOpenAssignedCases || 0} / {member.assignedCases || 0}
          </p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg,rgb(62, 217, 245) 0%, #3b82f6 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>All Cases Resolved</h3>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
            {member.resolvedCases || 0}
          </p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg,rgb(82, 247, 192) 0%, #059669 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Resolution Rate</h3>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
            {resolutionRate}%
          </p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg,rgb(239, 192, 110) 0%, #d97706 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Average Resolution Time</h3>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
            {formatResolutionTime(member.avgResolutionTime || 0)}
          </p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg,rgb(239, 143, 226) 0%,rgb(229, 61, 241) 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>Average First Response Time</h3>
          <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
            {formatResolutionTime(member.avgFirstResponseTime || 0)}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#4b5563' }}>
          Case Handling Efficiency
        </h3>
        <div style={{ height: '250px' }}>
          <Line data={casesChartData} options={chartOptions} />
        </div>
      </div>

      <div>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#4b5563' }}>
          Case Responsiveness Over Time
        </h3>
        <div style={{ height: '250px' }}>
          <Line data={resolutionTimeChartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default SupportMemberDetail;
